import axiosInstance from '@/axios/axios';
import { queryKeys } from '@/queries/keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// Define the response interface based on new backend structure
interface Permissions {
  id?: number;
  userId?: any;
  announcement: boolean;
  calendar: boolean;
  schoolAdmin: boolean;
  addPeople: boolean;
  classTeacher: boolean;
  addSubject: boolean;
  previousExams: boolean;
  weeklyPlanner: boolean;
  divTransfer: boolean;
  schoolSettings: boolean;
  promotions: boolean;
  teacherAttendance: boolean;
  teacherAttendanceSheet: boolean;
  teacherUpdateAttendance: boolean;
  disciplineRecord: boolean;
  schoolFees: boolean;
  cashDeposit: boolean;
  leaves: boolean;
  reports: boolean;
  classWiseFeeReportPermission: boolean;
  busFeeReportPermission: boolean;
  dailyFinancialReportPermission: boolean;
  ClassWiseGradeReportPermission: boolean;
  gradeBySchoolYearPermission: boolean;
  rankingReportPermission: boolean;
  grReportPermission: boolean;
  studentListPermission: boolean;
}

interface AdminPermissionsResponse {
  status: number;
  permissions: Permissions;
}

// Request interface for the mutation
interface UpdatePermissionsRequest {
  username: string;
  permissions: Permissions;
}

export const useGetAdminPermissions = (username: string, enabled: boolean = true) => {
  return useQuery<AdminPermissionsResponse>({
    queryKey: queryKeys.profile.adminPermissions(username),
    queryFn: async (): Promise<AdminPermissionsResponse> => {
      const res = await axiosInstance.post(`/get-admin-permissions/`, {
        username,
      });
      return res.data;
    },
    enabled,
  });
};

export const useUpdateAdminPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, UpdatePermissionsRequest>({
    mutationFn: async (data): Promise<any> => {
      const res = await axiosInstance.post('/grant-admin-permission/', {
        username: data.username,
        ...data.permissions,
      });
      return res.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific user's permissions query
      queryClient.invalidateQueries({
        queryKey: queryKeys.profile.adminPermissions(variables.username),
      });
      toast.success('Permissions updated successfully');
    },
    onError: (error) => {
      toast.error(`Failed to update permissions: ${error.message}`);
    },
  });
};
